import { Link, Typography } from '@mui/material'
import React from 'react'

import { MUIExternalLink } from 'components/common/MUIExternalLink'
import Icon from 'components/Icon'
import { muiTheme } from 'mui-theme'
import { type TeamExternalIntegration } from 'types/api/status-checks/status-check'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'

export const URLHelperText: React.FC<{
  integration?: TeamExternalIntegration
}> = ({ integration }) => {
  switch (integration?.type) {
    case IntegrationType.Datadog:
    case IntegrationType.DatadogUS3:
    case IntegrationType.DatadogUS5:
    case IntegrationType.DatadogEU:
    case IntegrationType.DatadogUS1Fed:
      return (
        <Typography
          component="span"
          data-testid="url-helper-text-datadog"
          fontSize="smaller"
        >
          Look for help in{' '}
          <Link
            href="https://docs.datadoghq.com/api/latest/monitors/#get-a-monitors-details"
            target="_blank"
          >
            Datadog’s documentation
          </Link>
          . Looking to work with Datadog’s API? Go back one step and select
          “Custom” Observability Tool.
        </Typography>
      )
    case IntegrationType.Pagerduty:
      return (
        <Typography
          component="span"
          data-testid="url-helper-text-pagerduty"
          fontSize="smaller"
        >
          Look for help in{' '}
          <Link
            href="https://www.gremlin.com/docs/platform/integrations/pagerduty/"
            target="_blank"
          >
            PagerDuty Team Integration
          </Link>
        </Typography>
      )
    case IntegrationType.Newrelic:
      return (
        <Typography
          component="span"
          data-testid="url-helper-text-newrelic"
          fontSize="smaller"
        >
          Look for help in New Relic’s{' '}
          <Link
            href="https://docs.newrelic.com/docs/apis/rest-api-v2/application-examples-v2/application-reporting-health-status-v2/"
            target="_blank"
          >
            Application
          </Link>{' '}
          and Alerts documentation
        </Typography>
      )
    case IntegrationType.Grafana:
      return (
        <Typography
          component="span"
          data-testid="url-helper-text-grafana"
          fontSize="smaller"
        >
          See our{' '}
          <MUIExternalLink href="https://www.gremlin.com/docs/platform-health-checks-grafana-cloud">
            documentation
            <Icon
              css={{
                fontSize: 'inherit',
                marginLeft: muiTheme.spacing(0.5)
              }}
              icon="arrowUpRightFromSquare"
            />
          </MUIExternalLink>{' '}
          for how to create a Grafana health check. For general Grafana help,
          see the Grafana docs on{' '}
          <MUIExternalLink href="https://grafana.com/docs/grafana/latest/alerting/">
            Application and Alerts
            <Icon
              css={{
                fontSize: 'inherit',
                marginLeft: muiTheme.spacing(0.5)
              }}
              icon="arrowUpRightFromSquare"
            />
          </MUIExternalLink>
          .
        </Typography>
      )
    case IntegrationType.Dynatrace:
      return (
        <Typography
          component="span"
          data-testid="url-helper-text-dynatrace"
          fontSize="smaller"
        >
          Look for help in Dynatrace’s{' '}
          <Link
            href="https://www.dynatrace.com/support/help/how-to-use-dynatrace/synthetic-monitoring"
            target="_blank"
          >
            Synthetic monitoring
          </Link>{' '}
          documentation
        </Typography>
      )
    case IntegrationType.Appdynamics:
      return (
        <Typography
          component="span"
          data-testid="url-helper-text-appdynamics"
          fontSize="smaller"
        >
          Look for help in AppDynamics{' '}
          <Link
            href="https://docs.appdynamics.com/appd/4.5.x/en/appdynamics-essentials/alert-and-respond"
            target="_blank"
          >
            Alert and Respond
          </Link>{' '}
          documentation
        </Typography>
      )
    default:
      return null
  }
}
